import { NextPage } from 'next';
import { NextSeo } from 'next-seo';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { Container } from '@/components/core/Grid';
import { H1, H2, H3 } from '@/components/core/Heading';
import { ContentContainer } from '@/components/Partners/ContentContainer';
import { ContentPanters } from '@/components/Partners/ContentPartners';
import { StyledHeader } from '@/components/Partners/StyledHeader';
import { StyledImage } from '@/components/Partners/StyledImage';
import { StyledSubheader } from '@/components/Partners/StyledSubheader';
import { TechologyPartners } from '@/components/Partners/TechologyPartners';
import { Layout } from '@/components/ui/shared/layouts/Layout/Layout';
import { AccessFrom } from '@/enums/AccessFrom';

interface IPartner {
  file: string;
  alt: string;
  href: string;
}

const techologyPartners: Array<IPartner> = [
  {
    file: 'amazon.svg',
    alt: 'Amazon',
    href: 'https://aws.amazon.com/',
  },
  {
    file: 'datacite.svg',
    alt: 'DataCite',
    href: 'https://datacite.org/',
  },
  {
    file: 'googlecloud.svg',
    alt: 'Google Cloud',
    href: 'https://cloud.google.com/',
  },
  {
    file: 'infinum.svg',
    alt: 'Infinum',
    href: 'https://infinum.com/',
  },
  {
    file: 'intechopen.svg',
    alt: 'Intechopen',
    href: 'https://www.intechopen.com/',
  },
  {
    file: 'orcid.svg',
    alt: 'ORCiD',
    href: 'https://orcid.org/',
  },
  {
    file: 'vonage.svg',
    alt: 'Vonage',
    href: 'https://www.vonage.com/',
  },
];

const contentPartners: Array<IPartner> = [
  {
    file: 'aaai.svg',
    alt: 'AAAI',
    href: 'https://www.aaai.org/',
  },
  {
    file: 'aip.svg',
    alt: 'AIP',
    href: 'https://www.aip.org/',
  },
  {
    file: 'insticc.svg',
    alt: 'Insticc',
    href: 'http://www.insticc.org/',
  },
];

const Partners: NextPage = () => {
  const { t } = useTranslation();

  return (
    <Fragment>
      <NextSeo
        title={t('partners.title')}
        openGraph={{
          url: `${process.env.NEXT_PUBLIC_BASE_URL}/partners`,
        }}
      />
      <Layout>
        <StyledHeader>
          <Container>
            <H1>{t('partners.title')}</H1>
          </Container>
        </StyledHeader>

        <StyledSubheader>
          <Container>
            <H2>{t('partners.subtitle')}</H2>
          </Container>
        </StyledSubheader>

        <ContentContainer>
          <H3>{t('partners.techologyTitle')}</H3>

          <TechologyPartners>
            {techologyPartners.map(({ file, alt, href }) => (
              <a key={alt} href={href} target="_blank" rel="noopener noreferrer">
                <StyledImage src={`/images/partners/${file}`} alt={alt} />
              </a>
            ))}
          </TechologyPartners>

          <H3>{t('partners.contentTitle')}</H3>
          <ContentPanters>
            {contentPartners.map(({ file, alt, href }) => (
              <a key={alt} href={href} target="_blank" rel="noopener noreferrer">
                <StyledImage src={`/images/partners/${file}`} alt={alt} />
              </a>
            ))}
          </ContentPanters>
        </ContentContainer>
      </Layout>
    </Fragment>
  );
};

export default Partners;
